import clsx from 'clsx'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  EditorState,
  convertToRaw,
  type RawDraftContentState,
  convertFromRaw,
} from 'draft-js'
import Editor from '@draft-js-plugins/editor'
import createMentionPlugin, {
  type MentionData,
} from '@draft-js-plugins/mention'

import { api } from '../../../../../../services/api'
import { EntryComponent } from './components/EntryComponent'
import type UserDomain from '../../../../../../types/domain/user-domain'
import type PaginationDomain from '../../../../../../types/domain/pagination-domain'

type TextareaSuggestableProps = {
  contentRaw?: RawDraftContentState
}

export type TextareaSuggestableActions = {
  getPlainText: () => string
  getRawContent: () => RawDraftContentState
  getExtractedMentions: () => Array<{
    id: number
    name: string
    email: string
  }>
  clear: () => void
}

type TextareaSuggestableRef = React.Ref<TextareaSuggestableActions>

const TextareaSuggestable = forwardRef(
  (
    { contentRaw }: TextareaSuggestableProps,
    forwardedRef: TextareaSuggestableRef
  ) => {
    const createEditorStateFromContentRaw = useCallback(() => {
      if (contentRaw) {
        return EditorState.createWithContent(convertFromRaw(contentRaw))
      }

      return EditorState.createEmpty()
    }, [contentRaw])

    const ref = useRef<Editor>(null)
    const [editorState, setEditorState] = useState(
      createEditorStateFromContentRaw
    )

    const [isOpen, setIsOpen] = useState(false)
    const [suggestions, setSuggestions] = useState<MentionData[]>([])

    const { MentionSuggestions, plugins } = useMemo(() => {
      const mentionPlugin = createMentionPlugin({
        mentionTrigger: '@',
        mentionComponent (mentionProps) {
          return (
            <span
              className={`${mentionProps.className} font-medium text-primary-light`}
            >
              {mentionProps.children}
            </span>
          )
        },
      })
      const { MentionSuggestions } = mentionPlugin
      const plugins = [mentionPlugin]

      return { plugins, MentionSuggestions }
    }, [])

    const onOpenChange = useCallback((_open: boolean) => {
      setIsOpen(_open)
    }, [])

    const onSearchChange = useCallback(async ({ value }: { value: string }) => {
      try {
        const response = await api.get<PaginationDomain<UserDomain>>(
          `/users/suggestions?search=${value}&take=6`
        )

        setSuggestions(
          response.data?.data?.map((user) => ({
            id: user.email,
            email: user.email,
            name: user.name,
          }))
        )
      } catch (error) {
        console.error(error)
        return []
      }
    }, [])

    useImperativeHandle(
      forwardedRef,
      () => {
        const currentContent = editorState.getCurrentContent()

        return {
          getPlainText () {
            return currentContent.getPlainText()
          },
          getRawContent () {
            return convertToRaw(currentContent)
          },
          getExtractedMentions () {
            const raw = convertToRaw(currentContent)

            let mentionedUsers = []

            for (const key in raw.entityMap) {
              const ent = raw.entityMap[key]

              if (ent.type === 'mention') {
                mentionedUsers.push(ent.data.mention)
              }
            }

            return mentionedUsers
          },
          clear () {
            // setEditorState(getResetEditorState(editorState))
            setEditorState(EditorState.createEmpty())
          },
        }
      },
      [editorState]
    )

    useEffect(() => {
      setEditorState(createEditorStateFromContentRaw)
    }, [contentRaw, createEditorStateFromContentRaw])

    return (
      <>
        <div className="relative">
          <div
            onClick={() => ref.current?.focus()}
            className={clsx(
              'w-full h-32 p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500 placeholder:text-grey-500 placeholder:font-sans placeholder:text-sm cursor-text',
              {
                'rounded-tl-none': isOpen && suggestions.length > 0,
              }
            )}
          >
            <div className="h-full overflow-y-auto scrollbar">
              <Editor
                editorKey={'editor'}
                placeholder="Escreva uma pergunta ou atualização"
                editorState={editorState}
                onChange={setEditorState}
                plugins={plugins}
                autoCapitalize={'none'}
                autoComplete={'off'}
                autoCorrect={'off'}
                spellCheck={false}
                ref={ref}
              />
              <MentionSuggestions
                open={isOpen}
                onOpenChange={onOpenChange}
                suggestions={suggestions}
                onSearchChange={onSearchChange}
                entryComponent={EntryComponent}
                popoverContainer={({ children }) => {
                  return (
                    <div className="absolute top-0 left-0 p-4 pr-2 -translate-y-full bg-white border border-b-0 rounded-t-lg shadow-inner border-grey-500 min-w-96">
                      <div className="pr-2 space-y-2 overflow-y-auto max-h-52 scrollbar">
                        {children}
                      </div>
                    </div>
                  )
                }}
              />
            </div>
          </div>
        </div>
      </>
    )
  }
)

TextareaSuggestable.displayName = 'TextareaSuggestable'

export { TextareaSuggestable }
